import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFavoriteTests, toggleFavorite } from '../../api/client';
import { Heart, Clock, Award, BookOpen, Loader2 } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

interface Test {
  _id: string;
  title: string;
  description: string;
  image?: string;
  category: string;
  timeLimit: number;
  points: number;
  rating?: number;
}

const FavoritesTests: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      const data = await getFavoriteTests();
      setTests(data);
    } catch (err) {
      showToast('Не удалось загрузить избранное', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const handleRemoveFavorite = async (e: React.MouseEvent, testId: string) => {
    e.stopPropagation();
    try {
      await toggleFavorite(testId);
      setTests(prev => prev.filter(t => t._id !== testId));
      showToast('Удалено из избранного', 'success');
    } catch (err) {
      showToast('Не удалось удалить', 'error');
    }
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </>
    );
  }

  return (
    <>
      <div style={styles.header}>
        <h1 style={styles.title}>Избранные тесты</h1>
      </div>

      {tests.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999' }}>Нет избранных тестов</p>
      ) : (
        <div style={styles.grid}>
          {tests.map(test => (
            <div key={test._id} style={styles.card} onClick={() => navigate(`/app/elearning/tests/${test._id}`)}>
              <div style={{ position: 'relative' }}>
                <img src={test.image || '/images/default-test.jpg'} alt={test.title} style={styles.cardImage} />
                <button
                  onClick={(e) => handleRemoveFavorite(e, test._id)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'rgba(255,255,255,0.8)',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    cursor: 'pointer',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <Heart size={18} fill="#ff4d4f" color="#ff4d4f" />
                </button>
              </div>
              <div style={styles.cardContent}>
                <h3 style={styles.cardTitle}>{test.title}</h3>
                <p style={styles.cardDesc}>{test.description}</p>
                <div style={styles.cardMeta}>
                  <span><Clock size={14} /> {test.timeLimit} мин</span>
                  <span><Award size={14} /> {test.points} баллов</span>
                </div>
                <span style={styles.categoryBadge}>{test.category}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 700, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24 },
  card: { background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(8px)', borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)', cursor: 'pointer', transition: 'transform 0.2s' },
  cardImage: { width: '100%', height: 160, objectFit: 'cover' },
  cardContent: { padding: 20 },
  cardTitle: { fontSize: 18, marginBottom: 8, color: '#1a1a2e' },
  cardDesc: { fontSize: 14, color: '#666', marginBottom: 16 },
  cardMeta: { display: 'flex', gap: 16, fontSize: 13, color: '#888', marginBottom: 8 },
  categoryBadge: { display: 'inline-block', padding: '4px 12px', background: 'rgba(37,117,252,0.1)', borderRadius: 20, fontSize: 12, color: '#2575fc', marginTop: 8 },
};

export default FavoritesTests;